import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Page from "../../components/Page";
import { toast } from "react-hot-toast";
import ApiClient from "../../helpers/ApiClient";
import AppLinksManager from "../../components/SuperAdmin/AppLinksManager";

export default function SuperAdminSettingsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    FONEPAY_API_KEY: "",
    FONEPAY_SECRET_KEY: "",
    FONEPAY_MERCHANT_CODE: "",
    SMS_GATEWAY_URL: "",
    SMS_GATEWAY_API_KEY: "",
    MAP_PROVIDER: "osm", // osm or mapbox
    MAPBOX_API_KEY: "",
    CHATBOT_WELCOME_MESSAGE: "",
  });
  const [appLinks, setAppLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await ApiClient.get("/superadmin/settings");
      if(response.data) {
        setSettings((prev) => ({ ...prev, ...response.data }));

        let parsedLinks = [];
        if (response.data.APP_DOWNLOAD_LINKS) {
          try {
            parsedLinks = typeof response.data.APP_DOWNLOAD_LINKS === 'string'
              ? JSON.parse(response.data.APP_DOWNLOAD_LINKS)
              : response.data.APP_DOWNLOAD_LINKS;
          } catch(e) {
            parsedLinks = [];
          }
        }
        if (!Array.isArray(parsedLinks) || parsedLinks.length === 0) {
          parsedLinks = [
            { id: "captain_app", name: "Captain App", download_link: response.data.CAPTAIN_APP_DOWNLOAD_LINK || "", manual_link: response.data.CAPTAIN_APP_MANUAL_LINK || "" },
            { id: "waiter_app", name: "Waiter App", download_link: response.data.WAITER_APP_DOWNLOAD_LINK || "", manual_link: response.data.WAITER_APP_MANUAL_LINK || "" },
            { id: "printer_driver", name: "Printer Driver", download_link: response.data.PRINTER_DRIVER_DOWNLOAD_LINK || "", manual_link: response.data.PRINTER_DRIVER_MANUAL_LINK || "" },
            { id: "windows_app", name: "Windows App", download_link: response.data.WINDOWS_APP_DOWNLOAD_LINK || "", manual_link: response.data.WINDOWS_APP_MANUAL_LINK || "" },
            { id: "linux_app", name: "Linux App", download_link: response.data.LINUX_APP_DOWNLOAD_LINK || "", manual_link: response.data.LINUX_APP_MANUAL_LINK || "" },
            { id: "mac_app", name: "Mac App", download_link: response.data.MAC_APP_DOWNLOAD_LINK || "", manual_link: response.data.MAC_APP_MANUAL_LINK || "" }
          ];
        }
        setAppLinks(parsedLinks);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    toast.loading(t("settings.please_wait"));
    try {
      const updatedSettings = {
        ...settings,
        APP_DOWNLOAD_LINKS: JSON.stringify(appLinks)
      };

      // Also sync legacy keys if matching items exist in appLinks
      const findLink = (idMatch, nameMatch) => {
        return appLinks.find(l => (l.id && l.id.toLowerCase() === idMatch) || (l.name && l.name.toLowerCase().includes(nameMatch)));
      };
      const captain = findLink("captain_app", "captain");
      if (captain) {
        updatedSettings.CAPTAIN_APP_DOWNLOAD_LINK = captain.download_link;
        updatedSettings.CAPTAIN_APP_MANUAL_LINK = captain.manual_link;
      }
      const waiter = findLink("waiter_app", "waiter");
      if (waiter) {
        updatedSettings.WAITER_APP_DOWNLOAD_LINK = waiter.download_link;
        updatedSettings.WAITER_APP_MANUAL_LINK = waiter.manual_link;
      }
      const printer = findLink("printer_driver", "printer");
      if (printer) {
        updatedSettings.PRINTER_DRIVER_DOWNLOAD_LINK = printer.download_link;
        updatedSettings.PRINTER_DRIVER_MANUAL_LINK = printer.manual_link;
      }
      const win = findLink("windows_app", "windows");
      if (win) {
        updatedSettings.WINDOWS_APP_DOWNLOAD_LINK = win.download_link;
        updatedSettings.WINDOWS_APP_MANUAL_LINK = win.manual_link;
      }
      const linux = findLink("linux_app", "linux");
      if (linux) {
        updatedSettings.LINUX_APP_DOWNLOAD_LINK = linux.download_link;
        updatedSettings.LINUX_APP_MANUAL_LINK = linux.manual_link;
      }
      const mac = findLink("mac_app", "mac");
      if (mac) {
        updatedSettings.MAC_APP_DOWNLOAD_LINK = mac.download_link;
        updatedSettings.MAC_APP_MANUAL_LINK = mac.manual_link;
      }

      const settingsArray = Object.keys(updatedSettings).map(key => ({
        key, value: updatedSettings[key]
      }));

      const res = await ApiClient.post("/superadmin/settings", { settings: settingsArray });

      toast.dismiss();
      if (res.data && res.data.success) {
        toast.success(t("settings.settings_saved_successfully") || "Settings saved successfully");
      } else {
        toast.error((res.data && res.data.message) || "Failed to save settings");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(t("settings.something_went_wrong_try_later") || "An error occurred");
    }
  };

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  if (isLoading) {
    return <Page className="px-8 py-6">Loading...</Page>;
  }

  return (
    <Page className="px-8 py-6">
      <h3 className="text-3xl font-light mb-6">Global Integrations API Settings</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* Payment Gateway */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <h4 className="text-xl font-medium mb-4 text-gray-700">Fonepay Integration</h4>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm text-gray-600">Merchant Code</label>
              <input type="text" name="FONEPAY_MERCHANT_CODE" value={settings.FONEPAY_MERCHANT_CODE || ""} onChange={handleChange} className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200" />
            </div>
            <div>
              <label className="block mb-1 text-sm text-gray-600">API Key</label>
              <input type="password" name="FONEPAY_API_KEY" value={settings.FONEPAY_API_KEY || ""} onChange={handleChange} className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200" />
            </div>
            <div>
              <label className="block mb-1 text-sm text-gray-600">Secret Key</label>
              <input type="password" name="FONEPAY_SECRET_KEY" value={settings.FONEPAY_SECRET_KEY || ""} onChange={handleChange} className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200" />
            </div>
          </div>
        </div>

        {/* Map Services */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <h4 className="text-xl font-medium mb-4 text-gray-700">Map & Location Services</h4>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm text-gray-600">Map Provider</label>
              <select name="MAP_PROVIDER" value={settings.MAP_PROVIDER || "osm"} onChange={handleChange} className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200">
                <option value="osm">OpenStreetMap (Free)</option>
                <option value="mapbox">MapBox</option>
                <option value="google">Google Maps</option>
              </select>
            </div>
            {settings.MAP_PROVIDER !== 'osm' && (
              <div>
                <label className="block mb-1 text-sm text-gray-600">Provider API Key</label>
                <input type="password" name="MAPBOX_API_KEY" value={settings.MAPBOX_API_KEY || ""} onChange={handleChange} className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200" />
              </div>
            )}
          </div>
        </div>

        {/* Chatbot Settings */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm md:col-span-2">
          <h4 className="text-xl font-medium mb-4 text-gray-700">Chatbot Settings</h4>
          <div>
            <label className="block mb-1 text-sm text-gray-600">Welcome Message</label>
            <textarea name="CHATBOT_WELCOME_MESSAGE" value={settings.CHATBOT_WELCOME_MESSAGE || ""} onChange={handleChange} rows="3" className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200" placeholder="e.g. welcome to ehospitalitynepal, we are ready to serve your hospitality"></textarea>
          </div>
        </div>

        {/* SMS Gateway */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm md:col-span-2">
          <h4 className="text-xl font-medium mb-4 text-gray-700">SMS / OTP Gateway</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-sm text-gray-600">Gateway URL</label>
              <input type="text" name="SMS_GATEWAY_URL" value={settings.SMS_GATEWAY_URL || ""} onChange={handleChange} placeholder="e.g. https://sms.aakashsms.com/sms/v3/send" className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200" />
            </div>
            <div>
              <label className="block mb-1 text-sm text-gray-600">Authentication Token / API Key</label>
              <input type="password" name="SMS_GATEWAY_API_KEY" value={settings.SMS_GATEWAY_API_KEY || ""} onChange={handleChange} className="block w-full rounded-lg px-4 py-2 text-gray-800 bg-gray-50 border border-gray-200" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 border-t pt-8">
        <AppLinksManager links={appLinks} onChange={setAppLinks} />
      </div>

      <div className="mt-8 flex justify-end">
        <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-medium transition active:scale-95 shadow-sm">
          Save Settings
        </button>
      </div>

    </Page>
  );
}
